import { Stripe, StripeElements } from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js/pure";

interface CreatePaymentIntentResponse {
  clientSecret: string;
}

interface CreateSubscriptionResponse {
  subscriptionId: string;
  clientSecret: string;
}

async function createCreditsPaymentIntent(packageKey: string): Promise<string> {
  const response = await fetch("/credits/api/create-payment-intent", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      packageKey,
    }),
  });
  const data: CreatePaymentIntentResponse = await response.json();
  return data.clientSecret;
}

async function createSubscription(priceId: string): Promise<string> {
  const response = await fetch("/api/create-subscription", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      priceId,
    }),
  });
  const data: CreateSubscriptionResponse = await response.json();
  return data.clientSecret;
}

async function createDonationPaymentIntent(
  donationId: string,
): Promise<string> {
  const response = await fetch("/api/donate/create-payment-intent", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      donationId,
    }),
  });
  const data: CreatePaymentIntentResponse = await response.json();
  return data.clientSecret;
}

async function getClientSecret(): Promise<string | null> {
  const packageKey = document.getElementById(
    "package-key",
  ) as HTMLInputElement | null;
  const priceId = document.getElementById(
    "price-id",
  ) as HTMLInputElement | null;
  const donationId = document.getElementById(
    "donation-id",
  ) as HTMLInputElement | null;

  if (packageKey) {
    return await createCreditsPaymentIntent(packageKey.value);
  }

  if (priceId) {
    return await createSubscription(priceId.value);
  }

  if (donationId) {
    return await createDonationPaymentIntent(donationId.value);
  }

  return null;
}

async function checkStatus(stripe: Stripe) {
  const clientSecret = new URLSearchParams(window.location.search).get(
    "payment_intent_client_secret",
  );
  if (!clientSecret) return;

  const { paymentIntent } = await stripe.retrievePaymentIntent(clientSecret);
  if (paymentIntent) {
    switch (paymentIntent.status) {
      case "succeeded":
        showMessage("Payment succeeded!");
        break;
      case "processing":
        showMessage("Your payment is processing.");
        break;
      case "requires_payment_method":
        showMessage("Your payment was not successeful, please try again.");
        break;
      default:
        showMessage("Something went wrong.");
        break;
    }
  }
}

function showMessage(text?: string) {
  if (!text) return;
  const element = document.getElementById("payment-message");
  if (element) {
    element.classList.remove("hidden");
    element.textContent = text;

    setTimeout(function () {
      element.classList.add("hidden");
      element.textContent = "";
    }, 4000);
  }
}

function setLoading(loading: boolean) {
  const submitButton = document.getElementById(
    "submit",
  ) as HTMLButtonElement | null;
  const submitButtonIcon = document.getElementById(
    "button-icon",
  ) as HTMLElement | null;
  const submitButtonSpinnerIcon = document.getElementById(
    "button-spinner-icon",
  ) as HTMLElement | null;
  const submitButtonText = document.getElementById(
    "button-text",
  ) as HTMLElement | null;
  if (loading) {
    if (submitButton) submitButton.disabled = true;
    if (submitButtonIcon) submitButtonIcon.classList.add("hidden");
    if (submitButtonSpinnerIcon)
      submitButtonSpinnerIcon.classList.remove("hidden");
    if (submitButtonText) submitButtonText.classList.add("hidden");
  } else {
    if (submitButton) submitButton.disabled = false;
    if (submitButtonIcon) submitButtonIcon.classList.remove("hidden");
    if (submitButtonSpinnerIcon)
      submitButtonSpinnerIcon.classList.add("hidden");
    if (submitButtonText) submitButtonText.classList.remove("hidden");
  }
}

export async function activatePaymentForm() {
  const form = document.getElementById(
    "payment-form",
  ) as HTMLFormElement | null;
  if (form) {
    const stripe = await loadStripe(__STRIPE_API_KEY__);
    if (stripe) {
      await checkStatus(stripe);

      let elements: StripeElements | undefined;

      async function handleSubmit(e: SubmitEvent) {
        e.preventDefault();

        if (stripe && elements) {
          setLoading(true);

          const { error } = await stripe.confirmPayment({
            elements,
            confirmParams: {
              return_url: `${window.location.host}/credits/payment-complete/stripe`,
            },
          });

          if (
            error.type === "card_error" ||
            error.type === "validation_error"
          ) {
            showMessage(error.message);
          } else {
            showMessage("An unexpected error occurred.");
          }

          setLoading(false);
        }
      }

      form.addEventListener("submit", handleSubmit);
      const styles = window.getComputedStyle(form);

      const clientSecret = await getClientSecret();
      if (clientSecret) {
        console.log("got client secret");
        elements = stripe.elements({
          appearance: {
            variables: {
              colorPrimary: styles.getPropertyValue("--theme-ac-1"),
              colorBackground: styles.getPropertyValue("--theme-bg-1"),
              colorText: styles.getPropertyValue("--theme-fg-1"),
              fontFamily: styles.getPropertyValue("font-family"),
            },
          },
          clientSecret,
        });

        elements
          ?.create("payment", {
            layout: "tabs",
          })
          .mount("#payment-element");
      }
    }
  }
}
